<template>
  <div v-if="visible" class="chart-popup draggable-popup">
    <div class="chart-header draggable-header">
      <div class="popup-title">Balances Chart</div>
      <div class="chart-header-actions">
        <button
          type="button"
          class="close-button"
          title="Print chart"
          @click="printChart"
        >
          <i class="fas fa-print"></i>
        </button>
        <button @click="closePopup" class="close-button">X</button>
      </div>
    </div>
    <div class="input-row">
      <span class="from-to-label">
        From:
        <input class="date-picker" type="date" v-model="localStartDate" /> To:
        <input class="date-picker" type="date" v-model="localEndDate" />
        <button class="update-button" @click="updateChartDates">
          <i style="color: white; font-size: 0.8em" class="fa fa-sync"></i>
        </button>
      </span>
    </div>
    <div class="chart-tabs">
      <button
        class="chart-tab"
        :class="{ 'chart-tab-active': activeTab === 'balances' }"
        @click="switchTab('balances')"
      >
        Balances
      </button>
      <button
        class="chart-tab"
        :class="{ 'chart-tab-active': activeTab === 'cycles' }"
        @click="switchTab('cycles')"
      >
        Credits by Cycle
      </button>
    </div>
    <div
      v-show="activeTab === 'balances'"
      ref="chartRef"
      style="width: 100%; height: 450px"
    ></div>
    <div
      v-show="activeTab === 'cycles'"
      ref="cycleChartRef"
      style="width: 100%; height: 450px"
    ></div>
    <div v-if="chartError" class="chart-error-overlay">
      <div class="chart-error-box">
        <div class="chart-error-title">Chart Error</div>
        <div class="chart-error-msg">{{ chartError }}</div>
        <button class="chart-error-dismiss" @click="chartError = null">
          Dismiss
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, watch, nextTick } from 'vue';
import axios from '@/axiosConfig';
import Plotly from 'plotly.js-dist';

export default {
  name: 'ChartPopup',
  props: {
    visible: {
      type: Boolean,
      required: true,
    },
    startDate: {
      type: String,
      required: true,
    },
    endDate: {
      type: String,
      required: true,
    },
  },
  setup(props, { emit }) {
    const normalizeStartDateYear = (dateStr) => {
      if (!dateStr) return dateStr;
      const parsed = new Date(dateStr);
      if (Number.isNaN(parsed.getTime())) return dateStr;
      if (parsed.getFullYear() < 2024) {
        parsed.setFullYear(2026);
        return parsed.toISOString().split('T')[0];
      }
      return dateStr;
    };

    const chartData = ref(null);
    const chartRef = ref(null);
    const cycleChartRef = ref(null);
    const chartError = ref(null);
    const localStartDate = ref(
      normalizeStartDateYear(
        localStorage.getItem('chartStartDate') || props.startDate,
      ),
    );
    const localEndDate = ref(
      localStorage.getItem('chartEndDate') || props.endDate,
    );
    const activeTab = ref(localStorage.getItem('chartActiveTab') || 'balances');

    // Persist date/tab changes to localStorage
    watch(localStartDate, (val) => {
      const normalized = normalizeStartDateYear(val);
      if (normalized !== val) {
        localStartDate.value = normalized;
        return;
      }
      localStorage.setItem('chartStartDate', normalized);
    });
    watch(localEndDate, (val) => localStorage.setItem('chartEndDate', val));
    watch(activeTab, (val) => localStorage.setItem('chartActiveTab', val));

    // Watchers for localStartDate and localEndDate — debounced so typing/arrow keys don't trigger mid-edit
    let dateChangeTimer = null;
    watch([localStartDate, localEndDate], () => {
      if (dateChangeTimer) clearTimeout(dateChangeTimer);
      dateChangeTimer = setTimeout(async () => {
        await fetchData();
        renderChart();
        renderCycleChart();
      }, 2000);
    });

    const fetchData = async () => {
      const token = localStorage.getItem('token');
      try {
        const validatedStartDate = normalizeStartDateYear(localStartDate.value);
        if (validatedStartDate !== localStartDate.value) {
          localStartDate.value = validatedStartDate;
        }

        const response = await axios.get('/api/fetchBankDataforChart', {
          params: {
            start: validatedStartDate,
            end: localEndDate.value,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const rawData = response.data;

        // Object to group transactions by date
        const groupedData = {};

        // Process each transaction
        rawData.forEach((transaction) => {
          const date = transaction.BankRefDatetime.split(' ')[0]; // Extract date part only
          if (!groupedData[date]) {
            groupedData[date] = {
              Credit: 0,
              Debit: 0,
              Transactions: [],
            };
          }
          groupedData[date].Credit += parseFloat(transaction.Credit) || 0;
          groupedData[date].Debit += parseFloat(transaction.Debit) || 0;
          groupedData[date].Transactions.push(transaction);
        });

        // Prepare the final chart data with the highest Id transaction for ClosingBalance
        const chartDataArray = Object.keys(groupedData).map((date) => {
          const transactionsForDate = groupedData[date].Transactions;

          // Find the transaction with the highest Id (latest transaction for the day)
          const latestTransaction = transactionsForDate.reduce(
            (max, transaction) => {
              return !max || transaction.Id > max.Id ? transaction : max;
            },
            null,
          );

          const closingBalance = parseFloat(latestTransaction.Balance) || 0;

          return {
            date,
            Credit: groupedData[date].Credit.toFixed(2),
            Debit: groupedData[date].Debit.toFixed(2),
            ClosingBalance: closingBalance.toFixed(2),
          };
        });

        // Generate the full range of dates between the start and end dates
        const allDates = [];
        const startDate = new Date(localStartDate.value);
        const endDate = new Date(localEndDate.value);
        let currentDate = startDate;

        while (currentDate <= endDate) {
          allDates.push(currentDate.toISOString().split('T')[0]);
          currentDate.setDate(currentDate.getDate() + 1);
        }

        const filledData = [];
        let lastKnownClosingBalance = null;

        allDates.forEach((date) => {
          const dataForDate = chartDataArray.find((d) => d.date === date);
          if (dataForDate) {
            lastKnownClosingBalance = dataForDate.ClosingBalance;
            filledData.push(dataForDate);
          } else {
            filledData.push({
              date,
              Credit: '0.00',
              Debit: '0.00',
              ClosingBalance: lastKnownClosingBalance,
            });
          }
        });

        chartData.value = filledData;
      } catch (error) {
        console.error(error);
      }
    };

    const calculateAverageClosingBalance = (data) => {
      const validData = data.filter(
        (item) => !isNaN(parseFloat(item.ClosingBalance)),
      );
      const totalBalance = validData.reduce(
        (sum, item) => sum + parseFloat(item.ClosingBalance),
        0,
      );
      console.log(data);
      return validData.length ? totalBalance / validData.length : 0; // Avoid division by zero
    };

    // Returns the ISO date string of the cycle-start (25th) for a given date string
    const getCycleStartKey = (dateStr) => {
      const [year, month, day] = dateStr.split('-').map(Number);
      if (day >= 25) {
        return `${year}-${String(month).padStart(2, '0')}-25`;
      } else {
        const prevMonth = month === 1 ? 12 : month - 1;
        const prevYear = month === 1 ? year - 1 : year;
        return `${prevYear}-${String(prevMonth).padStart(2, '0')}-25`;
      }
    };

    const renderCycleChart = () => {
      if (!cycleChartRef.value || !chartData.value) return;
      try {
        // Build cycles: group dates by cycle start key, accumulate running credit total
        const cycleMap = {}; // cycleKey -> [{ date, runningTotal }]

        chartData.value.forEach((item) => {
          if (item.Type === 'Transfer') return; // exclude inter-account transfers
          const cycleKey = getCycleStartKey(item.date);
          if (!cycleMap[cycleKey]) cycleMap[cycleKey] = [];
          cycleMap[cycleKey].push({
            date: item.date,
            dailyCredit: parseFloat(item.Credit),
          });
        });

        // Sort cycle keys chronologically
        const sortedCycleKeys = Object.keys(cycleMap).sort();

        // Generate one Plotly trace per cycle
        const cycleColors = [
          '#4b0082',
          '#6a1a9a',
          '#7b2fbe',
          '#9b59b6',
          '#a67cb3',
          '#c39bd3',
          '#d7bde2',
          '#e8daef',
        ];

        const traces = sortedCycleKeys.map((cycleKey, idx) => {
          const days = cycleMap[cycleKey].sort((a, b) =>
            a.date.localeCompare(b.date),
          );
          let running = 0;
          const xs = [];
          const ys = [];
          const texts = [];
          days.forEach((d) => {
            running += d.dailyCredit;
            xs.push(d.date);
            ys.push(parseFloat(running.toFixed(2)));
            // Label: "MM/DD – $X,XXX"
            const [y, m, dd] = d.date.split('-');
            texts.push(
              `${m}/${dd}/${y.slice(2)}  $${running.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            );
          });

          // Cycle label: "MM/DD/YY – MM/DD/YY"
          const [cy, cm, cd] = cycleKey.split('-');
          const cycleLabel = `Cycle ${cm}/${cd}/${cy.slice(2)}`;

          const traceColor = cycleColors[idx % cycleColors.length];
          const markerColors = xs.map((d) => {
            const day = parseInt(d.split('-')[2], 10);
            if (day === 6) return '#1565c0';
            if (day === 12) return '#66bb6a';
            return traceColor;
          });
          const markerSizes = xs.map((d) => {
            const day = parseInt(d.split('-')[2], 10);
            return day === 6 || day === 12 ? 8 : 4;
          });

          return {
            x: xs,
            y: ys,
            type: 'scatter',
            mode: 'lines+markers',
            name: cycleLabel,
            text: texts,
            hovertemplate: '%{text}<extra></extra>',
            line: { color: traceColor, width: 2 },
            marker: { size: markerSizes, color: markerColors },
          };
        });

        const layout = {
          title: 'Running Credit Totals by Billing Cycle (25th–24th)',
          xaxis: { title: 'Date', type: 'date' },
          yaxis: {
            title: 'Cumulative Credits',
            tickformat: '$,.0f',
            automargin: true,
          },
          legend: { orientation: 'h', y: -0.2 },
          margin: { t: 50, b: 80 },
        };

        Plotly.newPlot(cycleChartRef.value, traces, layout, {
          displayModeBar: false,
        });
      } catch (e) {
        console.error('renderCycleChart error:', e);
        chartError.value =
          'Could not render the Credits by Cycle chart. Please check that your date range is valid and try again.';
      }
    };

    const renderChart = () => {
      if (!chartRef.value || !chartData.value) {
        return;
      }
      try {
        const averageClosingBalance = calculateAverageClosingBalance(
          chartData.value,
        );
        const formattedAverage = `$${averageClosingBalance.toLocaleString(
          undefined,
          { minimumFractionDigits: 2, maximumFractionDigits: 2 },
        )}`;

        const plotData = [
          {
            x: chartData.value.map((item) => item.date),
            y: chartData.value.map((item) => item.Credit),
            type: 'bar',
            name: 'Credit',
            marker: { color: 'green' },
            hovertemplate: '%{x|%m/%d/%y}  $%{y:,.2f}<extra></extra>',
          },
          {
            x: chartData.value.map((item) => item.date),
            y: chartData.value.map((item) => item.Debit),
            type: 'bar',
            name: 'Debit',
            marker: { color: 'red' },
            hovertemplate: '%{x|%m/%d/%y}  $%{y:,.2f}<extra></extra>',
          },
          {
            x: chartData.value.map((item) => item.date),
            y: chartData.value.map((item) => item.ClosingBalance),
            type: 'bar',
            name: 'Balance',
            marker: { color: '#cccccc' },
            hovertemplate: '%{x|%m/%d/%y}  $%{y:,.2f}<extra></extra>',
          },
          {
            x: [
              chartData.value[0].date,
              chartData.value[chartData.value.length - 1].date,
            ],
            y: [averageClosingBalance, averageClosingBalance],
            type: 'scatter',
            mode: 'lines',
            name: 'Average Balance',
            line: { color: '#643e81', width: 2 },
            hovertemplate: 'Average Closing Balance: $%{y:,.2f}<extra></extra>',
          },
        ];

        const layout = {
          title: 'Bank Balances Over Time',
          barmode: 'relative', // Grouped bars
          xaxis: {
            title: 'Date',
          },
          yaxis: {
            range: [-65000, 130000], // Primary y-axis range
            automargin: true, // Automatically manage margins
            ticklabelpadding: 30, // Padding between tick labels and the axis
            title: {
              text: 'Amount',
              standoff: 20, // Extra space between the axis title and tick labels
            },
          },
          yaxis2: {
            title: 'Balance',
            overlaying: 'y',
            side: 'right',
            range: [
              0,
              Math.max(...chartData.value.map((item) => item.Balance)) * 1.1,
            ], // Adjusted range for Balance
          },
          bargap: 0.2, // Adjust this value to control bar width
          bargroupgap: 0.5, // Optional: Adjust this value to control group gap

          annotations: [
            {
              x: chartData.value[Math.floor(chartData.value.length / 9)].date,
              y: averageClosingBalance,
              text: `${formattedAverage}`,
              xref: 'x',
              yref: 'y',
              showarrow: true,
              ax: 0, // Position relative to the x-axis
              ay: -40, // Position above the line
              font: {
                color: '#643e81',
                size: 12,
              },
              align: 'left',
              borderwidth: 0,
              borderpad: 4,
              bgcolor: 'white',
              opacity: 0.8,
            },
          ],
        };
        Plotly.newPlot(chartRef.value, plotData, layout, {
          displayModeBar: false,
        });

        // Add click event listener with double-click detection
        let lastClickTime = 0;
        chartRef.value.on('plotly_click', (data) => {
          if (data && data.points && data.points.length > 0) {
            const now = new Date().getTime();
            const DOUBLE_CLICK_THRESHOLD = 300; // Milliseconds to detect double-click

            if (now - lastClickTime < DOUBLE_CLICK_THRESHOLD) {
              const date = data.points[0].x;
              emit('dateSelected', date); // Emit the dateSelected event with the clicked date
            }

            lastClickTime = now;
          }
        });
      } catch (e) {
        console.error('renderChart error:', e);
        chartError.value =
          'Could not render the Balances chart. Please check that your date range is valid and try again.';
      }
    };

    const switchTab = (tab) => {
      activeTab.value = tab;
      nextTick(() => {
        if (tab === 'balances') {
          renderChart();
        } else {
          renderCycleChart();
        }
      });
    };

    const makeDraggable = (el) => {
      if (!el || !(el instanceof HTMLElement)) return;

      let pos1 = 0,
        pos2 = 0,
        pos3 = 0,
        pos4 = 0;
      const dragElement = el.querySelector('.draggable-header');

      if (dragElement) {
        dragElement.onmousedown = dragMouseDown;
      } else {
        el.onmousedown = dragMouseDown;
      }

      function dragMouseDown(e) {
        e.preventDefault();
        pos3 = e.clientX;
        pos4 = e.clientY;
        document.onmouseup = closeDragElement;
        document.onmousemove = elementDrag;
      }

      function elementDrag(e) {
        e.preventDefault();
        pos1 = pos3 - e.clientX;
        pos2 = pos4 - e.clientY;
        pos3 = e.clientX;
        pos4 = e.clientY;
        el.style.top = el.offsetTop - pos2 + 'px';
        el.style.left = el.offsetLeft - pos1 + 'px';
      }

      function closeDragElement() {
        document.onmouseup = null;
        document.onmousemove = null;
        ensurePopupOnScreen(el);
        localStorage.setItem('chartPopupTop', el.style.top);
        localStorage.setItem('chartPopupLeft', el.style.left);
      }
    };

    const ensurePopupOnScreen = (popup) => {
      if (!popup || !(popup instanceof HTMLElement)) return;

      const rect = popup.getBoundingClientRect();
      const isOccluded =
        rect.left < 0 ||
        rect.top < 0 ||
        rect.right > window.innerWidth ||
        rect.bottom > window.innerHeight;

      if (isOccluded) {
        popup.style.transform = 'none';
        popup.style.top = '0px';
        popup.style.left = '0px';
      }
    };

    const updateChartDates = async () => {
      await fetchData();
      renderChart();
      renderCycleChart();
    };

    const printChart = async () => {
      const el =
        activeTab.value === 'balances' ? chartRef.value : cycleChartRef.value;
      if (!el) return;
      try {
        const img = await Plotly.toImage(el, {
          format: 'png',
          width: 1100,
          height: 520,
        });
        const title =
          activeTab.value === 'cycles'
            ? 'Credits by Cycle'
            : 'Balances Chart';
        const range = `${localStartDate.value} to ${localEndDate.value}`;
        const w = window.open('', '_blank');
        if (!w) {
          alert('Please allow pop-ups to print the chart.');
          return;
        }
        w.document.write(`<!DOCTYPE html><html><head><title>${title}</title>
<style>
  body { margin: 16px; font-family: sans-serif; }
  h1 { font-size: 16pt; margin: 0 0 4px; }
  p { font-size: 10pt; color: #444; margin: 0 0 12px; }
  img { width: 100%; }
  @page { size: landscape; margin: 0.6cm; }
</style></head><body>
  <h1>${title}</h1>
  <p>${range}</p>
  <img src="${img}" alt="${title}" />
  <script>window.onload = function () { window.print(); }<\/script>
</body></html>`);
        w.document.close();
      } catch (err) {
        console.error('Error printing balances chart:', err);
      }
    };

    // Watch for changes in visibility and handle rendering and dragging
    watch(
      () => props.visible,
      async (newValue) => {
        if (newValue) {
          await nextTick();
          const popup = document.querySelector('.draggable-popup');
          const savedTop = localStorage.getItem('chartPopupTop');
          const savedLeft = localStorage.getItem('chartPopupLeft');
          if (popup && savedTop && savedLeft) {
            popup.style.top = savedTop;
            popup.style.left = savedLeft;
          }
          if (popup) {
            ensurePopupOnScreen(popup);
          }
          makeDraggable(popup);
          await fetchData();
          renderChart();
          renderCycleChart();
        }
      },
      { immediate: true },
    );
    return {
      chartRef,
      cycleChartRef,
      activeTab,
      switchTab,
      closePopup: () => {
        emit('update:visible', false); // Emit event to update visible state
        if (chartRef.value) {
          Plotly.purge(chartRef.value);
        }
        if (cycleChartRef.value) {
          Plotly.purge(cycleChartRef.value);
        }
      },
      localStartDate,
      localEndDate,
      updateChartDates,
      printChart,
      chartError,
    };
  },
};
</script>

<style scoped>
.popup-title {
  font-size: 18pt;
  color: white;
  margin-left: 20px;
  margin-top: 5px;
}

.chart-error-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
}

.chart-error-box {
  background: white;
  border: 1px solid #e57373;
  border-radius: 8px;
  padding: 20px 24px;
  max-width: 420px;
  text-align: center;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
}

.chart-error-title {
  font-weight: 700;
  font-size: 11pt;
  color: #b00020;
  margin-bottom: 8px;
}

.chart-error-msg {
  font-size: 9.5pt;
  color: #333;
  margin-bottom: 16px;
}

.chart-error-dismiss {
  border: 1px solid #b00020;
  background: #b00020;
  color: white;
  border-radius: 4px;
  padding: 5px 16px;
  cursor: pointer;
}

.chart-popup {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -60%);
  background: white;
  border: 2px solid #444;
  width: 1000px; /* Adjust width as needed */
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2); /* Horizontal, Vertical, Blur Radius, Spread Radius, Color */
  border-radius: 10px; /* Optional: Adds rounded corners */
  background-color: white; /* Optional: Sets background color for better contrast */
  padding-bottom: 5px;
}

.chart-header {
  width: 100%;
  display: flex;
  flex-flow: row;
  background: #333;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  justify-content: space-between;
}

.chart-header-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.close-button {
  background: #4b0082;
  color: white;
  border: 1pt solid #888;
  padding: 5px 10px;
  cursor: pointer;
  margin-right: 5px;
  height: 30px;
  padding-bottom: 3px;
  margin-top: 5px;
  margin-bottom: 5px;
  border-radius: 5px;
  font-size: 8pt;
}

.draggable-popup {
  position: absolute;
  user-select: none; /* Disable text selection inside draggable */
}

.draggable-header {
  cursor: move;
  user-select: none; /* Disable text selection inside draggable */
}

.input-row {
  margin-top: 5px;
  padding-left: 5px;
}
.input-row input {
  height: 25px;
  padding-left: 5px;
  border: 1pt solid #dddddd;
}

.update-button {
  margin-left: 5px;
  background: #4b0082;
  color: white;
  border: 1pt solid #888;
  padding: 5px 10px;
  cursor: pointer;
  margin-right: 5px;
  height: 28px;
  padding-bottom: 3px;
  margin-top: 5px;
  margin-bottom: 5px;
  border-radius: 5px;
}

.from-to-label {
  margin-left: 5px;
  font-size: 9pt;
}

.chart-tabs {
  display: flex;
  padding: 4px 8px 0;
  gap: 4px;
  border-bottom: 2px solid #ddd;
}

.chart-tab {
  padding: 4px 14px;
  background: #eee;
  border: 1px solid #ccc;
  border-bottom: none;
  border-radius: 4px 4px 0 0;
  cursor: pointer;
  font-size: 9pt;
}

.chart-tab-active {
  background: #4b0082;
  color: white;
  border-color: #4b0082;
}
</style>
